import {
  Flex,
  Table,
  Group,
  ActionIcon,
  Tooltip,
  Checkbox,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCopy, IconEdit, IconTrash } from "@tabler/icons-react";
import { YesNoDialog } from "components/YesNoDialog";
import dayjs from "dayjs";
import utcPlugin from "dayjs/plugin/utc";
import { useQueryCurrentUser } from "hooks/users";
import { useState } from "react";
import { limitTextLength } from "utils";
import { useAppTranslation } from "../../hooks/useTranslation";

dayjs.extend(utcPlugin);

interface EventsListProps {
  events: any[];
  batchAction: string | null;
  batchEvents: number[];
  organizerView: boolean;
  onEventChosen: (uid: string) => void;
  onEventDelete: (uid: string) => void;
  onEventCopy: (uid: string) => void;
  onBatchEventChosen: (selectedRows: number[]) => void;
}

export const EventsList = (props: EventsListProps) => {
  const {
    events,
    batchAction,
    batchEvents,
    organizerView,
    onEventChosen,
    onEventDelete,
    onEventCopy,
    onBatchEventChosen,
  } = props;

  const { user } = useQueryCurrentUser();
  const [opened, { open, close }] = useDisclosure(false);
  const [deletedUid, setDeletedUid] = useState("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const { t } = useAppTranslation();

  const deleteEventDialog = (uid: string) => {
    setDeletedUid(uid);
    open();
  };
  const deleteEvent = () => {
    onEventDelete(deletedUid);
  };
  return (
    <>
      <Flex direction="column" justify="space-between">
        <Table
          horizontalSpacing="md"
          verticalSpacing="xs"
          miw={700}
          highlightOnHover
          // layout="fixed"
          styles={{ td: { paddingTop: 5, paddingBottom: 5 } }}
        >
          <Table.Thead>
            <Table.Tr>
              {batchAction && <Table.Th />}
              <Table.Th>{t("events.event_title")}</Table.Th>
              {user?.organizer_is === null && (
                <Table.Th>{t("events.organizer")}</Table.Th>
              )}
              <Table.Th>{t("events.venue")}</Table.Th>
              <Table.Th>{t("events.start_time")}</Table.Th>
              {!organizerView && (
                <>
                  <Table.Th>{t("events.views")}</Table.Th>
                  <Table.Th>{t("events.bookmarks")}</Table.Th>
                </>
              )}
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {events.map((event, pos) => {
              const onEventClicked = () => {
                onEventChosen(event.uid);
              };
              return (
                <Table.Tr
                  key={event.id}
                  bg={
                    batchEvents.includes(pos)
                      ? "var(--mantine-color-blue-light)"
                      : undefined
                  }
                >
                  {batchAction && (
                    <Table.Td>
                      <Checkbox
                        aria-label="Select row"
                        checked={batchEvents.includes(pos)}
                        onChange={(event) => {
                          const newSelectedRows = event.currentTarget.checked
                            ? [...selectedRows, pos]
                            : selectedRows.filter(
                                (position) => position !== pos
                              );
                          setSelectedRows(newSelectedRows);
                          onBatchEventChosen &&
                            onBatchEventChosen(newSelectedRows);
                        }}
                      />
                    </Table.Td>
                  )}
                  <Table.Td onClick={onEventClicked}>
                    {limitTextLength(event.title)}
                  </Table.Td>
                  {user?.organizer_is === null && (
                    <Table.Td onClick={onEventClicked}>
                      {limitTextLength(event.organizers?.name)}
                    </Table.Td>
                  )}
                  <Table.Td onClick={onEventClicked}>
                    {limitTextLength(event.venues?.name || event.venue_name)}
                  </Table.Td>
                  <Table.Td onClick={onEventClicked}>
                    {dayjs.utc(event.start_time).format("ddd, DD/MM HH:mm")}
                  </Table.Td>
                  {!organizerView && (
                    <>
                      <Table.Td onClick={onEventClicked}>
                        {event.new_views[0].count + event.views}
                      </Table.Td>
                      <Table.Td onClick={onEventClicked}>
                        {event.bookmarks[0].count}
                      </Table.Td>
                    </>
                  )}
                  <Table.Td style={{ width: "140px" }}>
                    <Group
                      style={{
                        display: "flex",
                        gap: "10px",
                      }}
                    >
                      <Tooltip label={t("tooltips.edit")} openDelay={500}>
                        <ActionIcon onClick={onEventClicked}>
                          <IconEdit />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("tooltips.copy")} openDelay={500}>
                        <ActionIcon
                          onClick={() => {
                            onEventCopy(event.uid);
                          }}
                        >
                          <IconCopy />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("tooltips.delete")} openDelay={500}>
                        <ActionIcon
                          onClick={() => {
                            deleteEventDialog(event.uid);
                          }}
                        >
                          <IconTrash />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Flex>

      <YesNoDialog
        opened={opened}
        title={t("common.delete") + " " + t("events.title").slice(0, -1)}
        question={t("events.delete_confirmation")}
        onClose={close}
        onYes={deleteEvent}
      />
    </>
  );
};
